import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useBreakpointValue} from '@chakra-ui/react'
import usePDCResponsiveElement from '../../hooks/use-pdc-responsive-element.js'
import PDCTitle from '../pdc-title/index.jsx'
import PDCImage from '../pdc-image/index.jsx'

/**
 * PDPPersonalizePush is a React functional component that renders a personalized push section for a product detail page (PDP).
 * It displays a responsive image alongside customizable textual content, such as a title, subtitle, and description, all styled according to the provided props.
 * The component supports dynamic image selection based on the current viewport breakpoint, skeleton loading to prevent content layout shift (CLS), and accessibility features like ARIA descriptions.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The main title text for the section.
 * @param {string} [props.titleHeadingLevel] - The heading level for the title (e.g., 'h1', 'h2').
 * @param {string} [props.titleFontColor] - The font color for the title.
 * @param {string} [props.supTitle] - The subtitle or descriptor displayed above the main title (supports HTML).
 * @param {string} [props.desc] - The description text displayed below the title (supports HTML).
 * @param {string} [props.backgroundColor] - The background color for the content area.
 * @param {string} [props.textColor] - The text color for the content area.
 * @param {string} [props.smallImage] - The image URL for small breakpoints.
 * @param {string} [props.mediumImage] - The image URL for medium breakpoints.
 * @param {string} [props.largeImage] - The image URL for large breakpoints.
 * @param {string} [props.xlargeImage] - The image URL for extra-large breakpoints.
 * @param {string} [props.xxlargeImage] - The image URL for double extra-large breakpoints.
 * @param {string} [props.imageTitle] - The title attribute for the image (for accessibility).
 * @param {string} [props.imageAlt] - The alt attribute for the image (for accessibility).
 * @param {Object} [props.custom] - An optional object to override image URLs for each breakpoint.
 * @param {number} [props.displayPriority] - If set, prioritizes image loading and skeleton display.
 *
 * @returns {JSX.Element} The rendered personalized push section, including a responsive image and customizable content.
 *
 * @example
 * <PDPPersonalizePush
 *   title="Personalize Your Product"
 *   supTitle="<span>New Arrival</span>"
 *   desc="<p>Customize this product to fit your style.</p>"
 *   smallImage="/img/small.jpg"
 *   mediumImage="/img/medium.jpg"
 *   largeImage="/img/large.jpg"
 *   backgroundColor="#f8f8f8"
 *   textColor="#222"
 *   imageAlt="Personalized product"
 *   displayPriority={1}
 * />
 */
export const PDPPersonalizePush = ({
    title,
    titleHeadingLevel,
    titleFontColor,
    supTitle,
    desc,
    backgroundColor,
    textColor,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    custom,
    displayPriority
}) => {
    const titleId = useId()

    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            width="100%"
            height={'auto'}
            position={'relative'}
        >
            <PDCImage
                currentImage={currentImage}
                imageWidths={['100vw', '100vw', '100vw', '816px', '816px', '50vw']}
                imageAlt={imageAlt}
                imageTitle={imageTitle}
                displayPriority={displayPriority}
                title={title}
                skeletonProps={{aspectRatio: '4 / 5', width: '100%'}}
            />
            <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems={'center'}
                py={['34px', null, '44px']}
                px={['10px', null, '35px']}
                backgroundColor={backgroundColor || 'white'}
                color={textColor || 'fullBlack'}
                position={'absolute'}
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width={[300, null, 350]}
            >
                <Text
                    dangerouslySetInnerHTML={{
                        __html: supTitle
                    }}
                    variant="descriptor"
                    color={textColor ? textColor : 'fullBlack'}
                    mb={'20px'}
                />
                <PDCTitle
                    title={title}
                    titleFontColor={titleFontColor}
                    titleHeadingLevel={titleHeadingLevel}
                    txtHorizontalPos={'center'}
                    textColor={textColor}
                    id={titleId}
                    mb={['34px', null, '68px']}
                    size={'4xl'}
                />
                <Text
                    dangerouslySetInnerHTML={{
                        __html: desc
                    }}
                    variant="bodyBase2"
                    color="darkGray"
                    textAlign={'center'}
                    mx={[20, null, 0]}
                />
            </Flex>
        </Box>
    )
}

PDPPersonalizePush.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    supTitle: PropTypes.string,
    desc: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    txtHorizontalPos: PropTypes.string,
    imgPlacement: PropTypes.string,
    wrapperImageUrl: PropTypes.string,
    custom: PropTypes.object,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    ),
    displayPriority: PropTypes.bool
}
